import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Loader2, CheckCircle2, AlertCircle, Bot } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface Props {
    restaurantId: string;
}

interface ReceptionistStatus {
    assistantId: string | null;
    phoneNumber: string | null;
    provisioningStatus?: "none" | "pending" | "completed" | "failed";
}

export function AdminReceptionistSettings({ restaurantId: _restaurantId }: Props) {
    const { toast } = useToast();

    // Fetch Vapi Status
    const { data: vapiStatus, isLoading: isStatusLoading } = useQuery<ReceptionistStatus>({
        queryKey: [`/api/receptionist/status`],
        refetchInterval: (data) => data?.provisioningStatus === 'pending' ? 3000 : false,
    });

    // Provisioning Mutation
    const provisionMutation = useMutation({
        mutationFn: async () => {
            const res = await apiRequest("POST", "/api/receptionist/provision");
            return res.json();
        },
        onSuccess: () => {
            toast({
                title: "Provisioning Started",
                description: "Your AI Receptionist is being set up. This will take about a minute.",
            });
            queryClient.invalidateQueries({ queryKey: [`/api/receptionist/status`] });
        },
        onError: (error: any) => {
            toast({
                title: "Provisioning Failed",
                description: error.message || "Failed to start provisioning. Please try again.",
                variant: "destructive",
            });
        }
    });

    return (
        <Card className="p-6">
            <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                    <div className="flex items-center gap-2">
                        <Bot className="h-5 w-5 text-primary" />
                        <h3 className="text-lg font-semibold">AI Receptionist Setup</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mr-8">
                        Automatically provision a dedicated phone number and AI assistant to handle calls, answer questions, and take reservations for your restaurant.
                    </p>

                    <div className="mt-4 flex flex-wrap gap-4 pt-2">
                        {isStatusLoading ? (
                            <div className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Checking status...
                            </div>
                        ) : vapiStatus?.provisioningStatus === 'completed' && vapiStatus.phoneNumber ? (
                            <div className="flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-3 py-1.5 rounded-lg text-sm font-medium border border-green-200 dark:border-green-800">
                                <CheckCircle2 className="h-4 w-4" />
                                Active Number: <span className="font-bold ml-1">{vapiStatus.phoneNumber}</span>
                            </div>
                        ) : vapiStatus?.provisioningStatus === 'pending' ? (
                            <div className="flex items-center gap-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 px-3 py-1.5 rounded-lg text-sm font-medium animate-pulse">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Setting up your assistant...
                            </div>
                        ) : vapiStatus?.provisioningStatus === 'failed' ? (
                            <div className="flex items-center gap-2 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 px-3 py-1.5 rounded-lg text-sm font-medium border border-red-200 dark:border-red-800">
                                <AlertCircle className="h-4 w-4" />
                                Provisioning Failed. Try again.
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground">
                                <Bot className="h-4 w-4 opacity-50" />
                                No phone number assigned yet
                            </div>
                        )}
                    </div>
                </div>

                <Button
                    disabled={vapiStatus?.provisioningStatus === 'pending' || provisionMutation.isPending || isStatusLoading}
                    onClick={() => provisionMutation.mutate()}
                    className="shadow-md whitespace-nowrap"
                >
                    {vapiStatus?.provisioningStatus === 'completed' ? 'Request a New Phone Number' : 'Activate AI Receptionist'}
                    {(vapiStatus?.provisioningStatus === 'pending' || provisionMutation.isPending) && (
                        <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                    )}
                </Button>
            </div>
        </Card>
    );
}
